import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EmptyState, ErrorState, Skeleton, cn } from '@vta/ui';
import type { TeamSummary } from '@vta/types';
import { apiClient } from '../../lib/api-client.ts';
import { formatProgramLabel, formatTeamNumber } from '../../lib/format.ts';

/**
 * Sibling teams from the same city (preferred) or country (fallback).
 *
 * The /api/teams endpoint already accepts `city`/`country` filters. We send a
 * small limit (12) since this surface is a side-rail, not a primary list.
 */
export interface RelatedTeamsProps {
  /** The team we are showing — excluded from the related list. */
  teamNumber: string;
  citySlug: string | null;
  countrySlug: string | null;
  className?: string;
}

interface TeamsResponse {
  teams: Array<TeamSummary & { teamNumber: string }>;
}

export function RelatedTeams({ teamNumber, citySlug, countrySlug, className }: RelatedTeamsProps) {
  const scope = citySlug ? { kind: 'city' as const, value: citySlug } : countrySlug ? { kind: 'country' as const, value: countrySlug } : null;

  const query = useQuery<TeamsResponse>({
    queryKey: ['related-teams', scope?.kind, scope?.value, teamNumber],
    enabled: !!scope,
    queryFn: ({ signal }) =>
      apiClient.get<TeamsResponse>('/api/teams', {
        query: scope?.kind === 'city' ? { city: scope.value, limit: 13 } : { country: scope?.value, limit: 13 },
        signal,
      }),
  });

  if (!scope) {
    return (
      <EmptyState
        title="No location on file"
        description="We need a city or country to show related teams."
        className={className}
      />
    );
  }

  if (query.isLoading) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-3/4" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load related teams"
        retry={() => query.refetch()}
        className={className}
      />
    );
  }

  const others = (query.data?.teams ?? []).filter((t) => t.teamNumber !== teamNumber).slice(0, 12);

  if (others.length === 0) {
    return (
      <EmptyState
        title={scope.kind === 'city' ? 'No siblings in this city' : 'No siblings in this country'}
        description="When more teams are catalogued they will appear here."
        className={className}
      />
    );
  }

  return (
    <ul className={cn('divide-y divide-border-subtle border-y border-border-subtle', className)}>
      {others.map((t) => (
        <li key={t.teamNumber} className="flex items-center justify-between gap-3 py-2.5">
          <Link
            to="/team/$teamNumber"
            params={{ teamNumber: t.teamNumber }}
            className="flex min-w-0 flex-col"
          >
            <span className="font-mono text-sm text-fg">{formatTeamNumber(t.teamNumber)}</span>
            <span className="truncate text-xs text-fg-subtle">{t.teamName}</span>
          </Link>
          <span className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">
            {formatProgramLabel(t.program, 'short')}
          </span>
        </li>
      ))}
    </ul>
  );
}
